import React, { useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Row, Col, Button, Spinner, Image, Form, Modal } from '@themesberg/react-bootstrap';
import { GetApiRequest, PutApiRequest, placeholderImageDataUrl } from '../services/api';
import { toast } from '../components/Toaster';
import { Routes } from '../routes';
import PhoneNumberInput from '../components/PhoneNumberInput';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faEnvelope, faPhone, faCalendar, faCrown, faPowerOff, faArrowLeft, faShieldAlt, faPen } from '@fortawesome/free-solid-svg-icons';

const AVATAR_PLACEHOLDER = placeholderImageDataUrl(150, 150);

const CATEGORY_OPTIONS = ['Food', 'Events', 'Outdoors', 'Nightlife', 'Wellness', 'Art', 'Hidden Gems', 'Family'];
const GO_OUT_OPTIONS = ['solo', 'couple', 'friend', 'family'];
const EXPERIENCE_OPTIONS = ['relaxing', 'adventurous', 'social', 'date night', '21'];
const PLANED_DAY_OPTIONS = ['Coffee', 'Beach day', 'market', 'hike'];

const EMPTY_PROFILE_FORM = { name: '', dateOfBirth: '', category: [], go_out: [], experience: [], planed_day: [] };

const VerifiedPill = ({ verified }) => (
  <span
    style={{
      display: 'inline-block',
      fontSize: '0.7rem',
      fontWeight: 600,
      padding: '0.1rem 0.5rem',
      borderRadius: '999px',
      background: verified ? '#dcfce7' : '#fef3c7',
      color: verified ? '#15803d' : '#b45309',
    }}
  >
    {verified ? 'Verified' : 'Unverified'}
  </span>
);

const UserDetails = () => {
  const { userId } = useParams();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedStatus, setSelectedStatus] = useState(true);
  const [profileForm, setProfileForm] = useState(EMPTY_PROFILE_FORM);
  const [contactForm, setContactForm] = useState({ email: '', phone: '' });
  const [updating, setUpdating] = useState(false);
  const [contactUpdating, setContactUpdating] = useState(false);

  const fetchUserDetails = async () => {
    try {
      const response = await GetApiRequest(`admin/users/${userId}`);
      setUser(response.data.data.user);
      setSelectedRole(response.data.data.user.role);
      setSelectedStatus(response.data.data.user.isActive);
    } catch (error) {
      console.error('Error fetching user details:', error);
      toast.error('Failed to load user details');
      history.push(Routes.Users.path);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchUserDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, history]);

  const handleRoleUpdate = async () => {
    setUpdating(true);
    try {
      await PutApiRequest(`admin/users/${userId}/role`, { role: selectedRole });
      toast.success('User role updated successfully');
      setShowRoleModal(false);
      fetchUserDetails();
    } catch (error) {
      console.error('Error updating role:', error);
      toast.error(error?.response?.data?.message || 'Failed to update user role');
    } finally {
      setUpdating(false);
    }
  };

  const openEditModal = () => {
    setProfileForm({
      name: user.name || '',
      dateOfBirth: user.dateOfBirth ? user.dateOfBirth.slice(0, 10) : '',
      category: user.category || [],
      go_out: user.go_out || [],
      experience: user.experience || [],
      planed_day: user.planed_day || [],
    });
    setSelectedStatus(user.isActive);
    setShowEditModal(true);
  };

  const toggleFormValue = (field, value) => {
    setProfileForm((prev) => {
      const current = prev[field];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      return { ...prev, [field]: next };
    });
  };

  const handleProfileUpdate = async () => {
    setUpdating(true);
    try {
      await PutApiRequest(`admin/users/${userId}/profile`, {
        name: profileForm.name,
        dateOfBirth: profileForm.dateOfBirth || null,
        category: profileForm.category,
        go_out: profileForm.go_out,
        experience: profileForm.experience,
        planed_day: profileForm.planed_day,
      });
      if (selectedStatus !== user.isActive) {
        await PutApiRequest(`admin/users/${userId}/status`, { isActive: selectedStatus });
      }
      toast.success('User profile updated successfully');
      setShowEditModal(false);
      fetchUserDetails();
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error?.response?.data?.message || 'Failed to update user profile');
    } finally {
      setUpdating(false);
    }
  };

  const openContactModal = () => {
    setContactForm({ email: user.email || '', phone: user.phone || '' });
    setShowContactModal(true);
  };

  const handleContactUpdate = async () => {
    setContactUpdating(true);
    try {
      const payload = {};
      if (contactForm.email && contactForm.email !== user.email) payload.email = contactForm.email;
      if (contactForm.phone && contactForm.phone !== user.phone) payload.phone = contactForm.phone;

      if (Object.keys(payload).length === 0) {
        setShowContactModal(false);
        return;
      }

      await PutApiRequest(`admin/users/${userId}/contact`, payload);
      toast.success('User contact info updated successfully');
      setShowContactModal(false);
      fetchUserDetails();
    } catch (error) {
      console.error('Error updating contact info:', error);
      toast.error(error?.response?.data?.message || 'Failed to update contact info');
    } finally {
      setContactUpdating(false);
    }
  };

  const getRoleColor = (role) => {
    const colors = {
      'admin': 'danger',
      'creators': 'primary',
      'local': 'info',
      'local-creators': 'warning',
      'user': 'success'
    };
    return colors[role] || 'secondary';
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div style={{ textAlign: 'center' }}>
          <Spinner animation="border" role="status" variant="primary" style={{ width: '3rem', height: '3rem' }} className="mb-3" />
          <p style={{ color: '#64748b' }}>Loading user details...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div style={{ padding: '2rem 0' }}>
        <div style={{ textAlign: 'center' }}>
          <h4 style={{ color: '#1e293b', marginBottom: '1rem' }}>User not found</h4>
          <Button 
            style={{ 
              background: '#4f46e5',
              border: 'none',
              borderRadius: '8px',
              fontWeight: 600,
              padding: '0.6rem 1.25rem'
            }} 
            onClick={() => history.push(Routes.Users.path)}
          >
            Back to Users
          </Button>
        </div>
      </div>
    );
  }

  const allowedRoles = ['user', 'creators', 'local', 'local-creators'];
  const canChangeRole = user.role !== 'admin';

  return (
    <div style={{ padding: '0 0 2rem 0' }}>
      <style>{`
        .user-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .back-btn {
          background: none;
          border: 1px solid #e2e8f0;
          color: #475569;
          border-radius: 8px;
          padding: 0.6rem 1.25rem;
          font-weight: 600;
          transition: all 0.3s ease;
          cursor: pointer;
        }

        .back-btn:hover {
          background: #f8fafc;
          border-color: #4f46e5;
          color: #4f46e5;
        }

        .user-page-title {
          font-size: 1.875rem;
          font-weight: 700;
          color: #1e293b;
          margin: 0;
        }

        .profile-card {
          background: white;
          border-radius: 12px;
          padding: 2rem;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          margin-bottom: 2rem;
        }

        .profile-header {
          display: flex;
          align-items: center;
          gap: 2rem;
          margin-bottom: 2rem;
          flex-wrap: wrap;
        }

        .profile-avatar {
          width: 150px;
          height: 150px;
          border-radius: 50%;
          object-fit: cover;
          border: 4px solid #4f46e5;
          box-shadow: 0 4px 12px rgba(79, 70, 229, 0.2);
          flex-shrink: 0;
        }

        .profile-info h3 {
          font-size: 1.75rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 0.5rem;
        }

        .profile-email {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: #64748b;
          margin-bottom: 1rem;
          font-size: 1rem;
        }

        .profile-badges {
          display: flex;
          gap: 0.75rem;
          flex-wrap: wrap;
        }

        .profile-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.6rem 1rem;
          border-radius: 8px;
          font-weight: 600;
          font-size: 0.9rem;
          color: white;
        }

        .action-buttons {
          display: flex;
          gap: 0.75rem;
          flex-wrap: wrap;
          margin-top: 1.5rem;
          padding-top: 1.5rem;
          border-top: 1px solid #f1f5f9;
        }

        .action-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          background: #4f46e5;
          color: white;
          border: none;
          border-radius: 8px;
          padding: 0.6rem 1.25rem;
          font-weight: 600;
          font-size: 0.9rem;
          transition: all 0.3s ease;
          cursor: pointer;
        }

        .action-btn:hover {
          background: #4338ca;
          transform: translateY(-1px);
        }

        .action-btn.secondary {
          background: #e2e8f0;
          color: #475569;
        }

        .action-btn.secondary:hover {
          background: #cbd5e0;
        }

        .info-section {
          background: white;
          border-radius: 12px;
          padding: 2rem;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          margin-bottom: 2rem;
        }

        .section-title {
          font-size: 1.1rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 1.5rem;
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .section-title svg {
          color: #4f46e5;
          font-size: 1.25rem;
        }

        .info-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem 0;
          border-bottom: 1px solid #f1f5f9;
        }

        .info-row:last-child {
          border-bottom: none;
        }

        .info-label {
          font-weight: 600;
          color: #475569;
          font-size: 0.95rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .info-value {
          color: #1e293b;
          font-size: 0.95rem;
        }

        .preferences-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1.5rem;
        }

        .preference-box {
          background: #f8fafc;
          border-radius: 12px;
          padding: 1.5rem;
          border: 1px solid #e2e8f0;
        }

        .preference-title {
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 1rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.95rem;
        }

        .preference-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
        }

        .pref-badge {
          display: inline-block;
          padding: 0.5rem 0.75rem;
          border-radius: 6px;
          font-size: 0.8rem;
          font-weight: 600;
          background: white;
          border: 1px solid #e2e8f0;
        }

        .modal-content {
          border-radius: 12px;
          border: none;
        }

        .modal-header {
          background: #4f46e5;
          color: white;
          border-radius: 12px 12px 0 0;
          padding: 1.25rem 1.5rem;
          border: none;
        }

        .modal-title {
          font-weight: 700;
          font-size: 1.1rem;
          color: white;
        }

        .modal-header .btn-close {
          filter: brightness(0) invert(1);
        }

        .form-label {
          font-weight: 600;
          color: #475569;
          margin-bottom: 0.75rem;
          font-size: 0.95rem;
        }

        .form-select, .form-control {
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          padding: 0.6rem 1rem;
          font-size: 0.95rem;
          transition: all 0.3s ease;
        }

        .form-select:focus, .form-control:focus {
          border-color: #4f46e5;
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
        }

        .form-text {
          font-size: 0.85rem;
        }

        .modal-footer {
          border-top: 1px solid #e2e8f0;
          padding: 1.25rem 1.5rem;
          background: #f8fafc;
        }

        @media (max-width: 768px) {
          .profile-header {
            flex-direction: column;
            align-items: center;
            text-align: center;
          }

          .profile-badges {
            justify-content: center;
          }

          .action-buttons {
            flex-direction: column;
          }

          .action-btn {
            justify-content: center;
          }

          .preferences-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      {/* Header */}
      <div className="user-header">
        <Button 
          className="back-btn"
          onClick={() => history.push(Routes.Users.path)}
        >
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          Back to Users
        </Button>
        <h2 className="user-page-title">User Profile</h2>
        <div style={{ width: '140px' }} />
      </div>

      {/* Profile Card */}
      <div className="profile-card">
        <div className="profile-header">
          <Image 
            src={user.avatar || AVATAR_PLACEHOLDER} 
            className="profile-avatar"
          />
          <div className="profile-info" style={{ flex: 1 }}>
            <h3>{user.name}</h3>
            <div className="profile-email">
              <FontAwesomeIcon icon={user.email ? faEnvelope : faPhone} />
              {user.email || user.phone || 'No contact info'}
            </div>
            <div className="profile-badges">
              <div className="profile-badge" style={{ background: `${getRoleColor(user.role) === 'danger' ? '#ef4444' : getRoleColor(user.role) === 'primary' ? '#4f46e5' : getRoleColor(user.role) === 'info' ? '#0ea5e9' : getRoleColor(user.role) === 'warning' ? '#f59e0b' : '#10b981'}` }}>
                <FontAwesomeIcon icon={faCrown} />
                {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
              </div>
              {!user.isActive && (
                <div className="profile-badge" style={{ background: '#ef4444' }}>
                  <FontAwesomeIcon icon={faPowerOff} />
                  Inactive
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="action-buttons">
              <button
                className="action-btn secondary"
                onClick={openEditModal}
              >
                <FontAwesomeIcon icon={faPen} />
                Edit Profile
              </button>
              {canChangeRole && (
                <button
                  className="action-btn"
                  onClick={() => setShowRoleModal(true)}
                >
                  <FontAwesomeIcon icon={faShieldAlt} />
                  Change Role
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Contact & Account Information */}
      <Row className="mb-4">
        <Col xs={12} md={6}>
          <div className="info-section">
            <div className="section-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>
                <FontAwesomeIcon icon={faPhone} />
                {' '}Contact Information
              </span>
              <button
                className="action-btn secondary"
                style={{ padding: '0.35rem 0.75rem', fontSize: '0.8rem' }}
                onClick={openContactModal}
              >
                <FontAwesomeIcon icon={faPen} />
                Edit Contact
              </button>
            </div>
            <div className="info-row">
              <span className="info-label">
                <FontAwesomeIcon icon={faEnvelope} style={{ fontSize: '0.9rem' }} />
                Email:
              </span>
              <span className="info-value" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                {user.email || 'Not provided'}
                <VerifiedPill verified={user.isEmailVerified} />
              </span>
            </div>
            <div className="info-row">
              <span className="info-label">
                <FontAwesomeIcon icon={faPhone} style={{ fontSize: '0.9rem' }} />
                Phone:
              </span>
              <span className="info-value" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                {user.phone || 'Not provided'}
                <VerifiedPill verified={user.isPhoneVerified} />
              </span>
            </div>
          </div>
        </Col>

        <Col xs={12} md={6}>
          <div className="info-section">
            <div className="section-title">
              <FontAwesomeIcon icon={faCalendar} />
              Account Information
            </div>
            <div className="info-row">
              <span className="info-label">Created:</span>
              <span className="info-value">{new Date(user.createdAt).toLocaleString()}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Last Updated:</span>
              <span className="info-value">{new Date(user.updatedAt).toLocaleString()}</span>
            </div>
          </div>
        </Col>
      </Row>

      {/* Preferences Section */}
      {(user.category?.length > 0 || user.go_out?.length > 0 || user.experience?.length > 0 || user.planed_day?.length > 0) && (
        <div className="info-section">
          <div className="section-title">
            <FontAwesomeIcon icon={faUser} />
            User Preferences
          </div>
          <div className="preferences-grid">
            {user.category && user.category.length > 0 && (
              <div className="preference-box">
                <div className="preference-title" style={{ color: '#6b7280' }}>
                  <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#6b7280' }} />
                  Categories
                </div>
                <div className="preference-tags">
                  {user.category.map((cat, index) => (
                    <span key={index} className="pref-badge" style={{ borderColor: '#cbd5e0', background: '#f3f4f6' }}>
                      {cat}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {user.go_out && user.go_out.length > 0 && (
              <div className="preference-box">
                <div className="preference-title" style={{ color: '#0ea5e9' }}>
                  <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#0ea5e9' }} />
                  Go Out Preferences
                </div>
                <div className="preference-tags">
                  {user.go_out.map((pref, index) => (
                    <span key={index} className="pref-badge" style={{ borderColor: '#bae6fd', background: '#f0f9ff' }}>
                      {pref}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {user.experience && user.experience.length > 0 && (
              <div className="preference-box">
                <div className="preference-title" style={{ color: '#f59e0b' }}>
                  <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#f59e0b' }} />
                  Experience Preferences
                </div>
                <div className="preference-tags">
                  {user.experience.map((exp, index) => (
                    <span key={index} className="pref-badge" style={{ borderColor: '#fde68a', background: '#fffbeb' }}>
                      {exp}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {user.planed_day && user.planed_day.length > 0 && (
              <div className="preference-box">
                <div className="preference-title" style={{ color: '#10b981' }}>
                  <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#10b981' }} />
                  Planned Day Activities
                </div>
                <div className="preference-tags">
                  {user.planed_day.map((activity, index) => (
                    <span key={index} className="pref-badge" style={{ borderColor: '#a7f3d0', background: '#f0fdf4' }}>
                      {activity}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Role Change Modal */}
      <Modal show={showRoleModal} onHide={() => setShowRoleModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Change User Role</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '2rem' }}>
          <Form.Group>
            <Form.Label className="form-label">Select Role</Form.Label>
            <Form.Select 
              className="form-select"
              value={selectedRole} 
              onChange={(e) => setSelectedRole(e.target.value)}
            >
              {allowedRoles.map(role => (
                <option key={role} value={role}>
                  {role.charAt(0).toUpperCase() + role.slice(1)}
                </option>
              ))}
            </Form.Select>
            <Form.Text className="form-text" style={{ marginTop: '0.75rem', display: 'block', color: '#64748b' }}>
              Current role: <strong style={{ color: '#1e293b' }}>{user.role}</strong>
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            style={{ 
              background: 'none', 
              border: '1px solid #e2e8f0', 
              color: '#475569',
              borderRadius: '8px',
              fontWeight: 600,
              padding: '0.6rem 1.25rem'
            }}
            onClick={() => setShowRoleModal(false)} 
            disabled={updating}
          >
            Cancel
          </Button>
          <Button 
            style={{ 
              background: '#4f46e5',
              border: 'none',
              color: 'white',
              borderRadius: '8px',
              fontWeight: 600,
              padding: '0.6rem 1.25rem'
            }}
            onClick={handleRoleUpdate} 
            disabled={updating || selectedRole === user.role}
          >
            {updating ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Updating...
              </>
            ) : (
              'Update Role'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Profile Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit User Profile</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '2rem' }}>
          <Form.Group className="mb-3">
            <Form.Label className="form-label">Name</Form.Label>
            <Form.Control
              type="text"
              value={profileForm.name}
              onChange={(e) => setProfileForm((prev) => ({ ...prev, name: e.target.value }))}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="form-label">Date of Birth</Form.Label>
            <Form.Control
              type="date"
              value={profileForm.dateOfBirth}
              onChange={(e) => setProfileForm((prev) => ({ ...prev, dateOfBirth: e.target.value }))}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="form-label">Status</Form.Label>
            <Form.Select
              className="form-select"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value === 'true')}
              disabled={user.role === 'admin'}
            >
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </Form.Select>
            {user.role === 'admin' && (
              <Form.Text className="form-text" style={{ marginTop: '0.5rem', display: 'block', color: '#64748b' }}>
                Admin accounts can't be deactivated.
              </Form.Text>
            )}
          </Form.Group>

          <Row>
            <Col xs={12} md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="form-label">Categories</Form.Label>
                <div>
                  {CATEGORY_OPTIONS.map((opt) => (
                    <Form.Check
                      key={opt}
                      type="checkbox"
                      id={`category-${opt}`}
                      label={opt}
                      checked={profileForm.category.includes(opt)}
                      onChange={() => toggleFormValue('category', opt)}
                    />
                  ))}
                </div>
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="form-label">Go Out Preferences</Form.Label>
                <div>
                  {GO_OUT_OPTIONS.map((opt) => (
                    <Form.Check
                      key={opt}
                      type="checkbox"
                      id={`go_out-${opt}`}
                      label={opt}
                      checked={profileForm.go_out.includes(opt)}
                      onChange={() => toggleFormValue('go_out', opt)}
                    />
                  ))}
                </div>
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="form-label">Experience Preferences</Form.Label>
                <div>
                  {EXPERIENCE_OPTIONS.map((opt) => (
                    <Form.Check
                      key={opt}
                      type="checkbox"
                      id={`experience-${opt}`}
                      label={opt}
                      checked={profileForm.experience.includes(opt)}
                      onChange={() => toggleFormValue('experience', opt)}
                    />
                  ))}
                </div>
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="form-label">Planned Day Activities</Form.Label>
                <div>
                  {PLANED_DAY_OPTIONS.map((opt) => (
                    <Form.Check
                      key={opt}
                      type="checkbox"
                      id={`planed_day-${opt}`}
                      label={opt}
                      checked={profileForm.planed_day.includes(opt)}
                      onChange={() => toggleFormValue('planed_day', opt)}
                    />
                  ))}
                </div>
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button
            style={{
              background: 'none',
              border: '1px solid #e2e8f0',
              color: '#475569',
              borderRadius: '8px',
              fontWeight: 600,
              padding: '0.6rem 1.25rem'
            }}
            onClick={() => setShowEditModal(false)}
            disabled={updating}
          >
            Cancel
          </Button>
          <Button
            style={{
              background: '#4f46e5',
              border: 'none',
              color: 'white',
              borderRadius: '8px',
              fontWeight: 600,
              padding: '0.6rem 1.25rem'
            }}
            onClick={handleProfileUpdate}
            disabled={updating || profileForm.name.trim().length < 2}
          >
            {updating ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Contact Modal */}
      <Modal show={showContactModal} onHide={() => setShowContactModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Contact Info</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '2rem' }}>
          <div
            style={{
              background: '#fef3c7',
              color: '#92400e',
              borderRadius: '8px',
              padding: '0.75rem 1rem',
              fontSize: '0.85rem',
              marginBottom: '1.5rem',
            }}
          >
            This changes what the user signs in with immediately — no verification code is sent.
          </div>

          <Form.Group className="mb-3">
            <Form.Label className="form-label">Email</Form.Label>
            <Form.Control
              type="email"
              value={contactForm.email}
              onChange={(e) => setContactForm((prev) => ({ ...prev, email: e.target.value }))}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="form-label">Phone</Form.Label>
            <PhoneNumberInput
              value={contactForm.phone}
              onChange={(phone) => setContactForm((prev) => ({ ...prev, phone }))}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            style={{
              background: 'none',
              border: '1px solid #e2e8f0',
              color: '#475569',
              borderRadius: '8px',
              fontWeight: 600,
              padding: '0.6rem 1.25rem',
            }}
            onClick={() => setShowContactModal(false)}
            disabled={contactUpdating}
          >
            Cancel
          </Button>
          <Button
            style={{
              background: '#4f46e5',
              border: 'none',
              color: 'white',
              borderRadius: '8px',
              fontWeight: 600,
              padding: '0.6rem 1.25rem',
            }}
            onClick={handleContactUpdate}
            disabled={contactUpdating}
          >
            {contactUpdating ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default UserDetails;